import { ReviewerStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

interface Reviewer {
  id: string;
  order: number;
  isStaff: boolean;
  [key: string]: any;
}

/**
 * Reconstructs reviewer list after removing a reviewer, updating order numbers appropriately
 * Staff reviewers maintain their position, non-staff reviewers get sequential numbering
 */
export function reconstructReviewersAfterRemoval<T extends Reviewer>(
  reviewers: T[],
  reviewerId: string
): T[] {
  const toRemove = reviewers.find((r) => r.id === reviewerId);

  // If removing a staff reviewer, just filter them out
  if (toRemove?.isStaff) {
    return reviewers.filter((r) => r.id !== reviewerId);
  }

  const filteredReviewers = reviewers.filter((r) => r.id !== reviewerId);

  // Rebuild order numbers for non-staff reviewers
  let hasStaff = false;
  const newReviewers = filteredReviewers.map((r, index) => {
    if (r.isStaff) {
      hasStaff = true;
      return r;
    }

    return {
      ...r,
      order: index + (hasStaff ? 0 : 1),
    };
  });

  return newReviewers;
}

interface FormReviewer {
  userId: string;
  reviewOrder: number;
  isRequired: boolean;
  isSigner: boolean;
  isStaff: boolean;
  dueDate?: DateTime;
  reviewerStatus?: ReviewerStatus | null;
}

interface SubmissionReviewerSigner {
  id: string;
  order: number;
  isStaff: boolean;
  isSigner?: boolean;
  dueDate?: DateTime;
  [key: string]: any;
}

/**
 * Maps reviewers and signer into the form format expected by the submission form
 * Combines both reviewers and signer into a single array with proper ordering
 */
export function buildFormReviewers(
  reviewers: SubmissionReviewerSigner[],
  signer: SubmissionReviewerSigner | null
): FormReviewer[] {
  const formReviewers: FormReviewer[] = [
    ...reviewers.map((r) => ({
      userId: r.id,
      reviewOrder: r.order,
      isRequired: true,
      isSigner: false,
      isStaff: r.isStaff,
      dueDate: r.dueDate,
      reviewerStatus: r.reviewerStatus ?? null,
    })),
    ...(signer
      ? [
          {
            userId: signer.id,
            reviewOrder: reviewers.length + 1,
            isRequired: true,
            isSigner: true,
            isStaff: signer.isStaff,
            dueDate: signer.dueDate,
            reviewerStatus: signer.reviewerStatus ?? null,
          },
        ]
      : []),
  ];

  return formReviewers;
}

/**
 * Reviewers the viewer is allowed to see. Encamp reviewers are an internal
 * detail of the review, so only staff see them — and anything that counts or
 * summarizes reviewers has to count the same list the customer is shown.
 */
export function visibleReviewers<T extends { isStaff?: boolean | null }>(
  reviewers: readonly T[],
  isStaff: boolean
): T[] {
  return reviewers.filter((reviewer) => isStaff || !reviewer.isStaff);
}
